import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Email from "./Email";
import Total from "./QuoteTotal";
import COLORS from "../colors";
import { deepFreeze, getDueDate, timestampToDate } from "../util/misc";
import { zipObject } from "lodash";

export const QUOTE_SENT_FIELDS = {
  date: null,
  delivered: null,
  recipients: [],
  followups: [],
  nonfinal: false,
  responseID: "",
};
deepFreeze(QUOTE_SENT_FIELDS);

export class QuoteSent extends DataStructure {
  constructor(parent, data = {}) {
    // Backward compatibility
    data = { ...data };
    if ("email" in data && data.email) {
      data.recipients = [{ email: data.email }];
    }
    if ("protected" in data) {
      data.nonfinal = Boolean(data.protected);
    }

    data = sanitize(data, QUOTE_SENT_FIELDS);

    // Convert to Date
    if (data.date) {
      data.date = timestampToDate(data.date);
    }
    if (data.delivered) {
      data.delivered = timestampToDate(data.delivered);
    }
    if (data.followups && data.followups.length) {
      data.followups.forEach((followup) => {
        followup.date = timestampToDate(followup.date);
      });
    }

    super(data);
    this._parent = parent;
  }

  get recipients() {
    return this.data.recipients.map((recipient) => ({
      contact: store.state.main.contacts[recipient.contact] || null,
      email: new Email(recipient.email),
    }));
  }

  get followups() {
    return this.data.followups.map((followup) => ({
      recipients: followup.recipients.map((recipient) => ({
        contact: store.state.main.contacts[recipient.contact] || null,
        email: new Email(recipient.email),
      })),
      date: followup.date,
    }));
  }

  get date() {
    return this.data.delivered || this._parent.hasResponse
      ? this.data.date
      : null;
  }

  get status() {
    if (!this.data.date) {
      return "unsent";
    } else if (!this.data.delivered && !this._parent.hasResponse) {
      return "undelivered";
    } else {
      return "sent";
    }
  }

  get statusType() {
    const type = QUOTE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }
}

export const QUOTE_RESPONSE_FIELDS = {
  isApproved: false,
  changes: [],
  submitted: null,
  declinedReason: "",
  expectCustomerContract: false,
  nonfinal: false,
  signature: null,
  total: null,
};
deepFreeze(QUOTE_RESPONSE_FIELDS);

export class QuoteResponse extends DataStructure {
  constructor(data = {}) {
    // Backward compatibility
    data = { ...data };
    if ("approved" in data) {
      data.isApproved = Boolean(data.approved);
    }
    if ("protected" in data) {
      data.nonfinal = Boolean(data.protected);
    }

    data = sanitize(data, QUOTE_RESPONSE_FIELDS);

    // Convert to Date
    data.submitted = timestampToDate(data.submitted);

    super(data);

    // Total
    this.data.total = this.total.data;
  }

  get date() {
    return this.data.submitted;
  }

  get status() {
    if (this.data.declinedReason) {
      return "declined";
    } else if (this.data.isApproved) {
      if (this.data.nonfinal) {
        return "nonfinal";
      } else if (this.data.changes && this.data.changes.length) {
        return "approved-changes";
      } else {
        return "approved";
      }
    } else if (this.data.submitted && this.data.changes.length) {
      return "declined-changes";
    } else {
      return "";
    }
  }

  get statusType() {
    const type = QUOTE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get total() {
    return new Total(this.data.total);
  }
}

export const QUOTE_STATUS_FIELDS = {
  type: "",
  sent: {},
  responses: {},
  original: null,
};
deepFreeze(QUOTE_STATUS_FIELDS);

export const QUOTE_STATUS_TYPES = {
  unsent: {
    order: 1,
    label: "Ready to send",
    icon: "unsent",
    color: "warning",
  },
  undelivered: {
    order: 2,
    label: "Never delivered",
    icon: "undelivered",
    color: "negative",
  },
  sent: {
    order: 3,
    label: "Sent",
    icon: "sent",
    color: "positive",
  },
  expired: {
    order: 4,
    label: "Expired",
    icon: "pending",
    color: "negative",
  },
  nonfinal: {
    order: 5,
    label: "Final quote requested",
    icon: "done",
    color: "warning",
  },
  "approved-changes": {
    order: 6,
    label: "Approved with changes",
    icon: "done",
    color: "warning",
  },
  approved: {
    order: 7,
    label: "Approved as-is",
    icon: "done",
    color: "positive",
  },
  "declined-changes": {
    order: 8,
    label: "Declined with changes",
    icon: "decline",
    color: "warning",
  },
  declined: {
    order: 9,
    label: "Declined",
    icon: "decline",
    color: "negative",
  },
};
deepFreeze(QUOTE_STATUS_TYPES);

export default class QuoteStatus extends DataStructure {
  constructor(parent, data = {}) {
    data = sanitize(data, QUOTE_STATUS_FIELDS);

    super(data);

    this._parent = parent;
    if (this.data.sent) {
      this.data.sent = this.sent.data;
    }
    this.data.responses = zipObject(
      Object.keys(this.data.responses),
      Object.values(this.data.responses).map((r) => new QuoteResponse(r).data)
    );
    this.data.type = this.get();
  }

  get() {
    const hasResponse = this.hasResponse;
    if (this.data.type.startsWith("approved")) {
      return this.data.type;
    } else if (this.data.sent && !hasResponse) {
      if (this._parent.expired) {
        return "expired";
      } else {
        return this.sent.status;
      }
    } else if (hasResponse) {
      return this.response.status;
    } else {
      return "unsent";
    }
  }

  get date() {
    const hasResponse = this.hasResponse;
    if (this.data.sent && !hasResponse) {
      if (this._parent.expired) {
        return getDueDate(this.sent.date, 30);
      } else {
        return this.sent.date;
      }
    } else if (hasResponse) {
      return this.response.date;
    }
    return null;
  }

  get reason() {
    const response = this.response;
    return response ? response.declinedReason : "";
  }

  // Formatting

  get type() {
    const type = QUOTE_STATUS_TYPES[this.data.type];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get sent() {
    return new QuoteSent(this, this.data.sent);
  }

  get hasResponse() {
    return this.data.sent && this.data.sent.responseID in this.data.responses;
  }

  get response() {
    return this.hasResponse
      ? new QuoteResponse(this.data.responses[this.data.sent.responseID])
      : null;
  }

  get responses() {
    return zipObject(
      Object.keys(this.data.responses),
      Object.values(this.data.responses).map((r) => new QuoteResponse(r))
    );
  }

  get original() {
    return this.data.original
      ? store.state.main.quotes[this.data.original]
      : null;
  }
}
